import { useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { URLS } from "MVVM/Utilities/Maps";
import { ProPlayerLink } from "MVVM/Components/ProContentLink";
import { Box, Flex, Text } from "rebass/styled-components";
import { useRecoilState } from "recoil";

import { getAvatarImage } from "MVVM/Utilities/User";
import { dateFormatWithDistanceSuffix } from "MVVM/Utilities/Clip";
import { FollowButton } from "MVVM/Components/Button";
import { useUser } from "MVVM/Hooks/useUser";
import { AllstarModalState } from "MVVM/State/modals";
import { IClip, ModalType } from "MVVM/@types";
import { useMutateFollowers } from "MVVM/Hooks/useMutateFollowers";
import { formatNumber } from "MVVM/Views/Wrapped/util";
import EditableText from "MVVM/Components/Text/Editable";
import { Clip } from "MVVM/Model/Clip";
import { getQueryParam } from "MVVM/Utilities/QueryParam";
import styled from "styled-components";
import { RiSeedlingFill } from "react-icons/ri";
import { Avatar } from "MVVM/Components/AvatarV2";
import { ClipBadges } from "MVVM/Components/ClipBadge";
import { CompetitionEligibility } from "MVVM/Components/CompetitionEligibility";
import { ScoreBreakdownModal } from "MVVM/Components/ScoreBreakdownModal";
import { FiBarChart2 } from "react-icons/fi";

interface InfoSectionProps {
  clip: IClip;
}

const InfoSection = ({ clip }: InfoSectionProps) => {
  const { allstarUser } = useUser();
  const { mutateFollowers } = useMutateFollowers();
  const editMode = getQueryParam("edit");
  const username = clip?.user?.username || clip?.username || "";
  const avatarImage = getAvatarImage(clip.user);
  const history = useHistory();
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const profileLink = `/profile?user=${clip?.user?._id}`;
  const handleClick = () => history.push(profileLink);
  const getTagValue = (key: string) =>
    clip.tags?.find((tag) => tag.key === key)?.value;
  const playerName = getTagValue("PARTNER_playerName");
  const playerAvatar =
    getTagValue("PARTNER_avatar") ||
    getTagValue("PARTNER_playerPhoto") ||
    undefined;
  const isPro = Boolean(playerName);
  const proPlayerId = getTagValue("PARTNER_playerId");
  const proContentUrl = proPlayerId
    ? `${URLS.TOP_PLAYERS}/${proPlayerId}`
    : null;
  const proProfileUrl = clip.playerGameIdentifier
    ? `/u/${clip.playerGameIdentifier}`
    : profileLink;
  const displayName = (isPro ? playerName : username) || "Allstar User";
  const displayAvatar = isPro ? playerAvatar : avatarImage;
  const goToCreator = () => history.push(isPro ? proProfileUrl : profileLink);
  const isUsersClip = clip.user?._id === allstarUser.user?._id;
  const clipFunctions = Clip.getInstance();
  const editTitle = clipFunctions.Title;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const isMontageClip = ["CAREER_MONTAGE", "FULL_MATCH_VOD"].includes(
    clip.type,
  );
  const isInternal = allstarUser?.user?.userGroup?.type === "INTERNAL";
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  const setFollowModal = ({
    displayName,
    avatar,
  }: {
    displayName: string;
    avatar: string;
  }) => {
    setAllstarModalState({
      ...allstarModalState,
      isOpen: ModalType.Signup,
      data: {
        target: displayName,
        avatar: avatar,
        action: "follow",
      },
    });
  };

  useEffect(() => {
    if (!editMode || !isUsersClip) return;
    if (containerRef.current) {
      const editableElement = containerRef?.current?.querySelector(
        "#react-edit-text-button",
      );
      if (editableElement) (editableElement as HTMLElement).click();
    }
  }, [editMode, isUsersClip]);

  return (
    <>
      <>
        {!!clip.remixOf && (
          <RemixBanner
            username={clip.remixOf?.user?.username}
            avatar={clip.remixOf?.user?.avatar || clip.remixOf?.user?.avatarUrl}
            userId={clip.remixOf?.user?._id}
          />
        )}

        {!isMobile && (
          <Flex style={{ gap: 18 }} p={6} pb={0}>
            <Flex flexGrow={1}>
              <Flex flexWrap="wrap" style={{ gap: 10 }} flexGrow={1}>
                <Avatar
                  src={displayAvatar}
                  size="md"
                  userGroup={isPro ? "PRO_PLAYER" : clip.user?.userGroup?.type}
                  rank={clip.user.rank}
                  profileCardUsername={
                    isPro ? undefined : clip.user.username || clip.user?._id
                  }
                  link={isPro ? proProfileUrl : profileLink}
                />
                <Flex
                  flexDirection="column"
                  flexGrow={1}
                  flexWrap="wrap"
                  style={{ gap: 10 }}
                >
                  <TitleContainer
                    justifyContent="space-between"
                    style={{ gap: 2, maxWidth: "100%" }}
                    ref={containerRef}
                    alignItems="flex-start"
                  >
                    <TitleWrapper>
                      {isUsersClip ? (
                        <EditableText
                          key={`edit-title-${clip.id}`}
                          variant="title3"
                          defaultValue={clip.title || ""}
                          onSave={(value: string) =>
                            editTitle(clip.internalId, value, isMontageClip)
                          }
                        />
                      ) : (
                        <Text variant="text.title3">{clip.title}</Text>
                      )}
                    </TitleWrapper>
                  </TitleContainer>
                  <Flex
                    style={{ gap: 4, fontWeight: 500, fontSize: 14 }}
                    color="chalk"
                    flexWrap="wrap"
                    alignItems="flex-start"
                  >
                    <Text onClick={goToCreator} style={{ cursor: "pointer" }}>
                      {displayName}
                    </Text>
                    <Text>&bull;</Text>
                    {!!clip.views && clip.views >= 5 && (
                      <>
                        <Text>{formatNumber(clip.views)} views</Text>{" "}
                        <Text>&bull;</Text>
                      </>
                    )}
                    <Text>{dateFormatWithDistanceSuffix(clip.createdAt)}</Text>
                    {isPro && (
                      <>
                        <Text>&bull;</Text>
                        <ViaCreator onClick={handleClick}>via HLTV</ViaCreator>
                      </>
                    )}
                  </Flex>
                  <ClipBadges
                    tags={clip.tags}
                    size="md"
                    maxVisible={3}
                    expandable
                  />
                  {proContentUrl && playerName && (
                    <ProPlayerLink
                      avatar={playerAvatar}
                      label={`More clips from ${playerName}`}
                      name={playerName}
                      to={proContentUrl}
                    />
                  )}
                  <Flex alignItems="center" style={{ gap: 8 }}>
                    {isInternal && (
                      <ScoreBreakdownButton
                        onClick={() => setBreakdownOpen(true)}
                      >
                        <FiBarChart2 size={13} />
                        Score breakdown
                      </ScoreBreakdownButton>
                    )}
                    <CompetitionEligibility shareId={clip.shareId} />
                  </Flex>
                </Flex>
                {!isUsersClip && (
                  <StyledFollowButton
                    alignSelf="flex-start"
                    allstarUser={allstarUser}
                    avatar={clip.user.avatar}
                    displayName={username}
                    flex={true}
                    followUnfollow={mutateFollowers}
                    followerData={undefined}
                    isModal={false}
                    ml={1}
                    py={5}
                    openModal={true}
                    setFollowModal={setFollowModal}
                    size="tiny"
                    userId={clip.user._id}
                  />
                )}
              </Flex>
            </Flex>
          </Flex>
        )}
        {isMobile && (
          <Flex style={{ gap: 8 }} p={6} pb={0} flexDirection="column">
            <Flex style={{ gap: 12 }}>
              <Box
                onClick={goToCreator}
                style={{ cursor: "pointer" }}
                flexShrink={0}
                alignSelf="flex-start"
              >
                <Avatar
                  src={displayAvatar}
                  link={isPro ? proProfileUrl : profileLink}
                  size="md"
                  userGroup={isPro ? "PRO_PLAYER" : undefined}
                />
              </Box>
              <TitleContainer ref={containerRef} alignItems="center">
                <TitleWrapper>
                  {isUsersClip ? (
                    <EditableText
                      key={`edit-title-mobile-${clip.id}`}
                      variant="title3"
                      defaultValue={clip.title || ""}
                      onSave={(value: string) =>
                        editTitle(clip.internalId, value, isMontageClip)
                      }
                    />
                  ) : (
                    <Text variant="text.title3">{clip.title}</Text>
                  )}
                </TitleWrapper>
              </TitleContainer>
            </Flex>
            <Flex alignItems="center">
              <Flex flexWrap="wrap" style={{ gap: 14 }} flexGrow={1}>
                <Flex
                  flexDirection="column"
                  style={{ gap: 12 }}
                  flexGrow={1}
                  flexWrap="wrap"
                >
                  <Flex style={{ gap: 6 }} color="chalk" flexWrap="wrap">
                    <Text
                      onClick={goToCreator}
                      style={{ cursor: "pointer" }}
                      variant="text.subheader"
                    >
                      {displayName}
                    </Text>
                    <Text variant="text.subheader">&bull;</Text>
                    {!!clip.views && clip.views >= 5 && (
                      <>
                        <Text variant="text.subheader">
                          {formatNumber(clip.views)} views
                        </Text>{" "}
                        <Text variant="text.subheader">&bull;</Text>
                      </>
                    )}
                    <Text variant="text.subheader">
                      {dateFormatWithDistanceSuffix(clip.createdAt)}
                    </Text>
                    {isPro && (
                      <>
                        <Text variant="text.subheader">&bull;</Text>
                        <ViaCreator onClick={handleClick}>via HLTV</ViaCreator>
                      </>
                    )}
                  </Flex>
                  <ClipBadges
                    tags={clip.tags}
                    size="md"
                    maxVisible={3}
                    expandable
                  />
                  {proContentUrl && playerName && (
                    <ProPlayerLink
                      avatar={playerAvatar}
                      label={`More clips from ${playerName}`}
                      name={playerName}
                      to={proContentUrl}
                    />
                  )}
                  <Flex alignItems="center" style={{ gap: 8 }}>
                    {isInternal && (
                      <ScoreBreakdownButton
                        onClick={() => setBreakdownOpen(true)}
                      >
                        <FiBarChart2 size={13} />
                        Score breakdown
                      </ScoreBreakdownButton>
                    )}
                    <CompetitionEligibility shareId={clip.shareId} />
                  </Flex>
                </Flex>
              </Flex>
              {!isUsersClip && (
                <StyledFollowButton
                  alignSelf="flex-start"
                  allstarUser={allstarUser}
                  avatar={clip.user.avatar}
                  displayName={username}
                  flex={true}
                  followUnfollow={mutateFollowers}
                  followerData={undefined}
                  isModal={false}
                  ml={1}
                  py={5}
                  openModal={true}
                  setFollowModal={setFollowModal}
                  size="tiny"
                  userId={clip.user._id}
                />
              )}
            </Flex>
          </Flex>
        )}
      </>
      <ScoreBreakdownModal
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        clipTitle={clip.title}
        totalScore={clip.score}
        breakdown={clip.scoreBreakdown}
      />
    </>
  );
};

const RemixBanner = ({
  avatar,
  username,
  userId,
}: {
  avatar: string;
  username: string;
  userId: string;
}) => (
  <StyledRemixBanner>
    <StyledRiSeedlingFill />
    <Text variant="text.body" fontWeight={500} color="chalk">
      Original Clip By:
    </Text>
    <Link to={`/u/${userId}`}>
      <Avatar src={avatar} size="sm" />
    </Link>
    <Text variant="text.body" fontWeight={500}>
      {username}
    </Text>
  </StyledRemixBanner>
);

const StyledRiSeedlingFill = styled(RiSeedlingFill)`
  color: ${({ theme }) => theme.colors.envy};
  height: 24px;
`;

const StyledRemixBanner = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.midnight};
  gap: 8px;
  padding: 8px 24px;
  width: 100%;
`;

const TitleContainer = styled(Flex)`
  align-items: flex-start;
`;

const TitleWrapper = styled(Box)`
  flex: 1;
  line-height: 0.8;
`;

const StyledFollowButton = styled(FollowButton)`
  font-size: 12px !important;
  margin: 0 !important;
  max-width: 40px !important;
  min-height: 28px !important;
  min-width: 70px !important;
  padding: 6px 8px !important;
`;

const ScoreBreakdownButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  margin-top: 6px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid rgba(143, 169, 175, 0.24);
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.chalk};
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.envyAlpha10};
    color: ${({ theme }) => theme.colors.envy};
    border-color: ${({ theme }) => theme.colors.envyAlpha40};
  }
`;

const ViaCreator = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-weight: 700;
  letter-spacing: 0.03em;
  opacity: 0.8;
  text-transform: uppercase;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
    opacity: 1;
  }
`;

export { InfoSection };
